import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import axiosInstance from '../../../utils/axios';
import { storeConstants } from '../../../utils/constants/StoreConstants';
import { URLS } from '../../../utils/constants/urls';

dayjs.extend(utc);

export const fetchVenues = () => (dispatch: any) => {
  return axiosInstance
    .get(URLS.venues)
    .then(({ status, data }) => {
      if (status === 200) {
        dispatch({
          type: storeConstants.FETCH_VENUES,
          payload: data,
        });
      }
    })
    .catch(() => {
      dispatch({
        type: storeConstants.FETCH_VENUES,
        payload: [],
      });
    });
};

export const fetchBookings = (filter: any) => (dispatch: any) => {
  return axiosInstance
    .post(URLS.bookings, filter)
    .then(({ status, data }) => {
      if (status === 200) {
        dispatch({
          type: storeConstants.FETCH_BOOKINGS,
          payload: createDataForBookings(data),
        });
      }
    })
    .catch(() => {
      dispatch({
        type: storeConstants.FETCH_BOOKINGS,
        payload: [],
      });
    });
};

const createDataForBookings = (bookings: any) => {
  let bookingArray = [];
  if (bookings && bookings.length) {
    bookingArray = bookings.map((booking: { start: string; end: string }) => {
      return {
        ...booking,
        start: dayjs.utc(booking.start.substring(0, 23)).local(),
        end: dayjs.utc(booking.end.substring(0, 23)).local(),
      };
    });
  }
  return bookingArray;
};

export const getSelectedMonthBooking = (date: any) => (dispatch: any) => {
  dispatch({
    type: storeConstants.CURRENT_MONTH_BOOKING,
    payload: date,
  });
};

type VenueApiItem = {
  id: number;
  name: string;
  children?: { id: number; name: string }[];
};

type VenueResource = {
  label: string;
  title: string;
  value: number;
  id: number;
  sortOrder: number;
  children?: VenueResource[];
};

const mapVenueToResource = (item: { id: number; name: string }, sortOrder: number): VenueResource => ({
  label: item.name,
  title: item.name,
  value: item.id,
  id: item.id,
  sortOrder,
});

/**
 * FullCalendar treats resource ids as globally unique. Child venues must not
 * also appear at the top level, or nested children are skipped when the id
 * was already registered (only later child ids may nest under the parent).
 */
export const buildCalendarVenueResources = (venues: VenueApiItem[]): VenueResource[] => {
  const childVenueIds = new Set(venues.flatMap(venue => (venue.children ?? []).map(child => child.id)));

  return venues
    .filter(venue => venue?.name && !childVenueIds.has(venue.id))
    .map((venue, idx) => {
      const resource = mapVenueToResource(venue, idx);
      if (venue.children?.length) {
        resource.children = venue.children.map((child, i) => mapVenueToResource(child, i));
      }
      return resource;
    });
};

export const buildVenueFilterOptions = (venues: VenueApiItem[]): VenueResource[] => {
  const seen = new Set<number>();
  const options: VenueResource[] = [];
  let sortOrder = 0;

  const addVenue = (item: { id: number; name: string }) => {
    if (!item?.name || seen.has(item.id)) {
      return;
    }
    seen.add(item.id);
    options.push(mapVenueToResource(item, sortOrder++));
  };

  venues.forEach(venue => {
    addVenue(venue);
    venue.children?.forEach(addVenue);
  });

  return options;
};

export const findVenueResourceById = (venues: VenueResource[], id: number): VenueResource | undefined =>
  venues.find(venue => venue.id === id);

/** Bookings on a parent venue have no parentId (null), not undefined. */
export const isParentVenueBooking = (item: { parentId?: number | null; venueId: number }): boolean =>
  item.parentId == null || item.parentId === item.venueId;
